/**
 * Notifications API Client
 *
 * API Endpoints:
 * - GET /notifications/list → Notification[]
 * - POST /notifications/read-all → void
 * - POST /notifications/{id}/read → void
 * - DELETE /notifications/delete-all → void
 * - DELETE /notifications/{id} → void
 * - GET /notifications/contacts → ContactPreferences
 */

import type { NotificationsEnvelope, RawNotification } from '@/types';
import { apiClient } from './base-client';

export type ContactPreferences = Record<string, boolean>;

/** Page size restoring pre-pagination parity (BE default dropped to 20, FRONT-244). */
const LIST_PER_PAGE = 50;

export const notificationsClient = {
  /**
   * Get the first page of notifications plus server totals. Tolerates the
   * legacy flat-array/`data` shapes by wrapping them in an empty envelope.
   */
  list: async (): Promise<NotificationsEnvelope> => {
    const data = await apiClient.get<
      (Partial<NotificationsEnvelope> & { data?: RawNotification[] }) | RawNotification[]
    >('/notifications/list', { params: { per_page: LIST_PER_PAGE } });
    if (Array.isArray(data)) return { notifications: data };
    if (!data || typeof data !== 'object') return { notifications: [] };

    const rows = [data.notifications, data.data].find(Array.isArray) ?? [];
    // BE's ApiNotificationResource serializes to `{}` when the referenced model
    // was deleted — drop those ghosts or they render blank and count as unread.
    const notifications = rows.filter((n): n is RawNotification => Boolean(n?.id));
    return {
      notifications,
      ...(data.pagination ? { pagination: data.pagination } : {}),
      ...(typeof data.unread_total === 'number' ? { unread_total: data.unread_total } : {}),
    };
  },

  /**
   * Mark all notifications as read
   */
  readAll: async (): Promise<void> => {
    await apiClient.post('/notifications/read-all');
  },

  /**
   * Mark a single notification as read
   */
  read: async (id: string): Promise<void> => {
    await apiClient.post(`/notifications/${id}/read`);
  },

  /**
   * Delete all notifications
   */
  deleteAll: async (): Promise<void> => {
    await apiClient.delete('/notifications/delete-all');
  },

  /**
   * Delete a single notification
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/notifications/${id}`);
  },

  /**
   * Get contact preferences
   */
  getContactPreferences: async (): Promise<ContactPreferences> => {
    const data = await apiClient.get<
      { contacts?: ContactPreferences; data?: ContactPreferences } | ContactPreferences
    >('/notifications/contacts');
    if (data && typeof data === 'object') {
      if ('contacts' in data && typeof data.contacts === 'object') return data.contacts;
      if ('data' in data && typeof data.data === 'object') return data.data;
      if (!Array.isArray(data) && !('contacts' in data) && !('data' in data))
        return data as ContactPreferences;
    }
    return {};
  },
};
